import { AfterContentInit, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CustomerObjectWithContactsDto } from "../../../../shared/models/customerObject/customer-object-with-contacts.dto";
import { KeyValueObject } from "../../../../shared/models/core/KeyValueObject";
import { CustomerObjectsService } from "../../../../categories/customer-objects/customer-objects.service";
import { DialogService } from "@progress/kendo-angular-dialog";
import { DropDownFilterSettings } from "@progress/kendo-angular-dropdowns";
import { CreateCustomerObjectComponent } from "../../../../shared/components/customer-object/create-customer-object/create-customer-object.component";
import { RequestType } from "../../../../shared/enums";
import { LookupService } from "../../../../shared/services/lookup.service";
import { DataSourceRequestState } from "@progress/kendo-data-query";

@Component({
	selector: 'new-request-customer-object-info',
	templateUrl: './new-request-customer-object-info.component.html',
	styleUrls: ['./new-request-customer-object-info.component.scss',
		'../../../../../vendor/libs/angular2-ladda/angular2-ladda.scss',
		'../../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
	]
})
export class NewRequestCustomerObjectInfoComponent implements AfterContentInit {

	clientDefaultItem: KeyValueObject = new KeyValueObject(0, 'Не выбрано');
	dropDownFilterSettings: DropDownFilterSettings = { caseSensitive: false, operator: 'contains' };

    @Input() selectedCustomerObjectId: number;
    @Input() selectedCustomerObject: CustomerObjectWithContactsDto = new CustomerObjectWithContactsDto();
    @Input() customerObjects: KeyValueObject[] = [];
    @Input() contragentId: number;
    @Input() required: number;
    @Input() requestTypeId: number;
    @Input() selectedServiceCenterId: number;
    @Input() showHeaderIcon: boolean = true;
    @Input() isReadonly: boolean = false;

    @Output() saveCustomerObject: EventEmitter<CustomerObjectWithContactsDto> = new EventEmitter<CustomerObjectWithContactsDto>();
    @Output() saveServiceCenter: EventEmitter<number> = new EventEmitter<number>();

    selectedCustomerObjectCoordinates: number[] = [];
    selectedCustomerObjectHasCoordinates: boolean = false;

    serviceCenters: KeyValueObject[] = [];

    constructor(
        public customerObjectsService: CustomerObjectsService,
        public dialogService: DialogService,
        public lookupService: LookupService
    ) { }

    ngAfterContentInit(): void {
        if (this.selectedCustomerObjectId) {
            this.customerObjectChange(this.selectedCustomerObjectId);
        }
        else {
            this.customerObjectChange(this.selectedCustomerObject.customerObjectId);
        }
        
        this.customerObjectsService.getAssignableServiceCenters().subscribe(resp => {
            this.serviceCenters = resp || [];
            this.ensureSelectedServiceCenterInList();

            if (this.selectedServiceCenterId == null) {
                let defaultServiceCenter = this.serviceCenters.find(x => x.isDefault);
                if (defaultServiceCenter == null && this.serviceCenters.length == 1) {
                    defaultServiceCenter = this.serviceCenters[0];
                }

                if (defaultServiceCenter != null) {
                    this.selectedServiceCenterId = defaultServiceCenter.id;
                    this.serviceCenterChange(this.selectedServiceCenterId);
                }
            }
        });
    }

    customerObjectChange(id: number) {
        if (id == null || id == 0) {
            this.selectedCustomerObject = new CustomerObjectWithContactsDto();
            this.saveCustomerObject.emit(null);
            this.refreshCoordinates();
        }
        else {
            this.customerObjectsService.getCustomerObjectWithContacts(id).subscribe((resp) => {
                if (resp.serviceCenterId && !this.serviceCenters.some(s => s.id === resp.serviceCenterId)) {
                    this.serviceCenters.push(new KeyValueObject(resp.serviceCenterId, resp.serviceCenterName));
                }

                if (!this.isReclamation && resp.serviceCenterId != null) {
                    this.selectedServiceCenterId = resp.serviceCenterId;
                    this.serviceCenterChange(this.selectedServiceCenterId);
                }
                
                this.commitCustomerObject(resp);
            });
        }
    }

    serviceCenterChange(id: number) {
        this.saveServiceCenter.emit(id);
    }

    private ensureSelectedServiceCenterInList() {
        if (this.selectedServiceCenterId == null || this.serviceCenters.some(s => s.id === this.selectedServiceCenterId)) {
            return;
        }

        const scState: DataSourceRequestState = {
            filter: {
                logic: 'and',
                filters: [{ field: 'ServiceCenterId', operator: 'eq', value: this.selectedServiceCenterId }]
            }
        };

        this.lookupService.getData("service-centers", scState).subscribe(data => {
            const item = (data || []).find(s => s.id === this.selectedServiceCenterId);
            if (item && !this.serviceCenters.some(s => s.id === item.id)) {
                this.serviceCenters.push(item);
            }
        });
    }

    customerObjectFilterChange(value: string) {
    }

    private createNewCustomerObjectDraft(): CustomerObjectWithContactsDto {
        const dto = new CustomerObjectWithContactsDto();
        dto.serviceCenterId = this.selectedServiceCenterId;
        return dto;
    }

    openCustomerObjectDialog(isNew: boolean) {
        const dialogRef = this.dialogService.open({ content: CreateCustomerObjectComponent, width: '60%', height: '720px' });
		const createContragentForm = <CreateCustomerObjectComponent>dialogRef.content.instance;

        createContragentForm.selectedCustomerObject = isNew
            ? this.createNewCustomerObjectDraft()
            : this.selectedCustomerObject;
		createContragentForm.customerObjects = this.customerObjects;
        createContragentForm.contragentId = this.contragentId;
        createContragentForm.isEditing = !isNew;
		createContragentForm.onCancelEvent.subscribe(() => dialogRef.close());
		createContragentForm.onContinueEvent.subscribe((customerObject) => {
            this.commitCustomerObject(customerObject);
			dialogRef.close();
		});
    }

    commitCustomerObject(customerObject: CustomerObjectWithContactsDto) {
        this.selectedCustomerObject = customerObject;
        this.saveCustomerObject.emit(this.selectedCustomerObject);
        this.refreshCoordinates();
    }

    refreshCoordinates() {
        if (this.selectedCustomerObject.latitude && 
            this.selectedCustomerObject.longitude) 
        {
            this.selectedCustomerObjectHasCoordinates = true;
            this.selectedCustomerObjectCoordinates = [
                this.selectedCustomerObject.latitude, 
                this.selectedCustomerObject.longitude];
        }
        else {
            this.selectedCustomerObjectCoordinates = [55.751952, 37.600739];
            this.selectedCustomerObjectHasCoordinates = false;
        }
    }

    get selectedCustomerObjectAddress() {
        if (!this.selectedCustomerObject.address) {
            return '-';
        }

        return this.selectedCustomerObject.addressDescription
            ? `${this.selectedCustomerObject.address}, ${this.selectedCustomerObject.addressDescription}`
            : this.selectedCustomerObject.address;
    }
    
    get isEditing(): boolean {
        return this.selectedCustomerObject.customerObjectId != null 
            && this.selectedCustomerObject.customerObjectId != 0;
    }

    get disabled(): boolean {
        return this.isReadonly || this.contragentId == null || this.contragentId == 0;
    }

    get isLaboratory(): boolean {
        return this.requestTypeId == <number>RequestType.laboratory;
    }

    get isReclamation(): boolean {
        return this.requestTypeId == <number>RequestType.reclamation;
    }
}